import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { theme, homeTheme, courseTheme } from './theme';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import RegisterOrg from './pages/Register/RegisterOrg';
import ThankYou from './pages/ThankYou/ThankYou';
import History from './pages/History/History';
import Reports from './pages/Reports/Reports';
import { Box, CircularProgress } from '@mui/material';
import { Footer } from './components/layout/Footer';
import Terms from './pages/Terms/Terms';
import Home from './pages/Home/Home';
import QuestionFlow from './components/QuestionFlow';
import FlowManagement from './pages/Admin/FlowManagement';
import ChatbotPage from './pages/Admin/ChatbotPage';
import { NavigationLayout } from './components/layout/NavigationLayout';
import QuestionManagement from './pages/Admin/QuestionManagement';
import DailyCheckinCustomization from './pages/DailyCheckinCustomization/DailyCheckinCustomization';
import DailyCheckinComplete from './pages/DailyCheckinComplete/DailyCheckinComplete';
import CourseContinue from './pages/CourseContinue';
import { useFlowCompletionStatus } from './hooks/useFlowCompletionStatus';
import { useAuthWithIntakeStatus } from './hooks/useAuthWithIntakeStatus';
import CourseManagement from './pages/Admin/CourseManagement';
import { TimezoneTest } from './components/TimezoneTest';
import CourseCompletion from './pages/CourseCompletion';
import AdminReports from './pages/Admin/AdminReports';
import ContentImport from './pages/Admin/ContentImport';
import QuizResults from './pages/QuizResults/QuizResults';

// Custom theme provider that switches between themes based on the route
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/home';
  const currentTheme = isHomePage ? homeTheme : courseTheme;
  
  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// Public Route component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const state = location.state as { from?: Location };
  
  if (isAuthenticated) {
    // Redirect to the page they were trying to visit before login
    // or to home if they directly accessed login/register
    return <Navigate to={state?.from?.pathname || "/home"} replace />;
  }
  
  return <>{children}</>;
};

// Protected Route component that checks for intake assessment completion
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    isAuthenticated, 
    isAuthLoading, // Added this line
    hasCompletedIntake, 
    intakeStatusLoading,
    intakeStatusError
  } = useAuthWithIntakeStatus(); // user is not destructured here in the original
  const location = useLocation();

  console.log('--- Debugging ProtectedRoute ---');
  console.log('isAuthLoading:', isAuthLoading);
  console.log('Current location.pathname:', location.pathname);
  console.log('isAuthenticated:', isAuthenticated);
  console.log('hasCompletedIntake:', hasCompletedIntake);
  console.log('intakeStatusLoading:', intakeStatusLoading);
  console.log('intakeStatusError:', intakeStatusError);

  // Wait for the initial authentication check to complete
  if (isAuthLoading) {
    console.log('ProtectedRoute: Auth status is loading, showing spinner...');
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    // Preserve the current location so we can redirect back after login
    console.log('ProtectedRoute: Not authenticated, redirecting to login.');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading indicator while checking flow completion status
  // Only show loading if we're not in an error state to prevent infinite loading
  if (intakeStatusLoading && !intakeStatusError) {
    console.log('ProtectedRoute: Intake status loading...');
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If intake assessment has never been completed and user isn't already on the intake flow, redirect them
  // Skip redirect if we're in an error state to prevent infinite loops
  if (!hasCompletedIntake && 
      !location.pathname.includes('/flows/intake_assessment_v0') && 
      !intakeStatusError) {
    console.log('CONDITION MET (ProtectedRoute): Redirecting to /flows/intake_assessment_v0');
    return <Navigate to="/flows/intake_assessment_v0" state={{ from: location }} replace />;
  } else if (isAuthenticated && !intakeStatusLoading) { // Log reasons for not redirecting
    console.log('CONDITION NOT MET (ProtectedRoute): Not redirecting to /flows/intake_assessment_v0. Reason(s):');
    if (hasCompletedIntake) console.log('  - hasCompletedIntake is true');
    if (location.pathname.includes('/flows/intake_assessment_v0')) console.log('  - already on intake flow path');
    if (intakeStatusError) console.log('  - intakeStatusError is true');
    console.log('Proceeding to render children or other routes in ProtectedRoute.');
  }

  return <>{children}</>;
};

// Require Intake Route component that checks for intake assessment completion
const RequireIntakeRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    isAuthenticated, 
    hasCompletedIntake, 
    intakeStatusLoading,
    intakeStatusError
  } = useAuthWithIntakeStatus();
  const location = useLocation();
  
  if (!isAuthenticated) {
    // Preserve the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading indicator while checking flow completion status
  // Only show loading if we're not in an error state to prevent infinite loading
  if (intakeStatusLoading && !intakeStatusError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If intake assessment has never been completed and user isn't already on the intake flow, redirect them
  // Skip redirect if we're in an error state to prevent infinite loops
  if (!hasCompletedIntake && 
      !location.pathname.includes('/flows/intake_assessment_v0') && 
      !intakeStatusError) {
    return <Navigate to="/flows/intake_assessment_v0" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Flow Route component to handle dynamic flow names
const FlowRoute: React.FC = () => {
  const { flowName } = useParams<{ flowName: string }>();
  const { 
    user, 
    hasCompletedIntake, 
    intakeStatusLoading,
    intakeStatusError
  } = useAuthWithIntakeStatus();
  
  // Show loading indicator while checking flow completion status
  // Only show loading if we're not in an error state to prevent infinite loading
  if (intakeStatusLoading && !intakeStatusError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Special handling for intake assessment
  // Skip redirect if we're in an error state to prevent infinite loops
  if (flowName === 'intake_assessment_v0' && 
      hasCompletedIntake && 
      !user?.is_staff && 
      !intakeStatusError) {
    // If they've already completed it and they're not an admin, send them home
    return <Navigate to="/home" />;
  }

  return <QuestionFlow flowName={flowName || ''} />;
};

// Admin Route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!user?.is_staff) {
    return <Navigate to="/home" />;
  }
  
  return <>{children}</>;
};

// Layout wrapper for protected pages with navigation
const HomeLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <NavigationLayout>{children}</NavigationLayout>;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <TimezoneProvider>
          <ThemedApp>
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
              <Routes>
                {/* Public routes without header */}
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                <Route path="/register/organization" element={<PublicRoute><RegisterOrg /></PublicRoute>} />
                <Route path="/organization" element={<Navigate to="/register/organization" replace />} />
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/thank-you" element={<ThankYou />} />

                {/* Protected routes with navigation layout */}
                <Route element={<HomeLayout><Outlet /></HomeLayout>}>
                  <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                  <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                  <Route path="/reports" element={
                    <ProtectedRoute>
                      <RequireIntakeRoute>
                        <Reports />
                      </RequireIntakeRoute>
                    </ProtectedRoute>
                  } />
                  <Route path="/timezone-test" element={<ProtectedRoute><TimezoneTest /></ProtectedRoute>} />
                  <Route path="/daily-checkin-customization" element={<ProtectedRoute><DailyCheckinCustomization /></ProtectedRoute>} />
                  <Route path="/course_complete" element={<ProtectedRoute><CourseCompletion /></ProtectedRoute>} />
                </Route>

                {/* Question flow routes - no sidebar */}
                <Route path="/weekly-check-in" element={<ProtectedRoute><QuestionFlow flowName="weekly_check_in" /></ProtectedRoute>} />
                <Route path="/flows/:flowName" element={<ProtectedRoute><FlowRoute /></ProtectedRoute>} />
                <Route path="/daily-checkin-complete" element={<ProtectedRoute><DailyCheckinComplete /></ProtectedRoute>} />
                <Route path="/course-continue" element={<ProtectedRoute><CourseContinue /></ProtectedRoute>} />
                <Route path="/quiz-results/:flowId" element={<ProtectedRoute><QuizResults /></ProtectedRoute>} />

                {/* Admin routes with navigation sidebar */}
                <Route path="/admin/flows" element={<HomeLayout><AdminRoute><FlowManagement /></AdminRoute></HomeLayout>} />
                <Route path="/admin/questions" element={<HomeLayout><AdminRoute><QuestionManagement /></AdminRoute></HomeLayout>} />
                <Route path="/admin/chatbot" element={<HomeLayout><AdminRoute><ChatbotPage /></AdminRoute></HomeLayout>} />
                <Route path="/admin/courses" element={<HomeLayout><AdminRoute><CourseManagement /></AdminRoute></HomeLayout>} />
                <Route path="/admin/reports" element={<HomeLayout><AdminRoute><AdminReports /></AdminRoute></HomeLayout>} />
                <Route path="/admin/content-import" element={<HomeLayout><AdminRoute><ContentImport /></AdminRoute></HomeLayout>} />
              </Routes>
            </Box>
          </ThemedApp>
        </TimezoneProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
